import "@/index.css";
import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { IntegrationsProvider } from "@/context/IntegrationsContext";
import { LocationProvider } from "@/context/LocationContext";
import { FiltersProvider } from "@/context/FiltersContext";
import { SavesProvider } from "@/context/SavesContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import RequireAuth from "@/components/auth/require_auth";
import RequireLister from "@/components/auth/require_lister";
import VerifyEmailGate from "@/components/auth/verify_email_gate";
import BotWidget from "@/components/bot/bot_widget";
import { PromoPopup } from "@/components/promo/promo_banner";
import NavBottom from "@/components/nav/nav_bottom";
import ScrollToTop from "@/components/util/scroll_to_top";
import NativePushBridge from "@/components/util/native_push_bridge";
import RouteSeo from "@/components/seo/route_seo";
import MarketPicker from "@/components/location/market_picker";
import { isAdminHost } from "@/lib/utils";

// Core public and renter pages stay in the main bundle so landing and the
// swipe experience paint instantly.
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/auth/LoginPage";
import SignupPage from "@/pages/auth/SignupPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import VerifyEmailPage from "@/pages/auth/VerifyEmailPage";
import PricingPage from "@/pages/PricingPage";
import SwipePage from "@/pages/SwipePage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import MatchesPage from "@/pages/MatchesPage";
import MessagesPage from "@/pages/MessagesPage";
import PaymentResultPage from "@/pages/PaymentResultPage";
import ChatPage from "@/pages/ChatPage";
import ProfileRenterPage from "@/pages/ProfileRenterPage";
import SavedPage from "@/pages/SavedPage";
import LikesPage from "@/pages/LikesPage";
import LikedYouPage from "@/pages/LikedYouPage";
import HostsPage from "@/pages/HostsPage";
import ListerPublicPage from "@/pages/ListerPublicPage";
import RealtorInvitePage from "@/pages/RealtorInvitePage";
import NotFoundPage from "@/pages/NotFoundPage";

const ProfileListerPage = lazy(() => import("@/pages/ProfileListerPage"));

// Everything heavy or off the hot path is code split.
const MapPage = lazy(() => import("@/pages/MapPage"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));

// Lister pages
const BecomeLister = lazy(() => import("@/pages/lister/BecomeLister"));
const BecomeListerPrivate = lazy(() => import("@/pages/lister/BecomeListerPrivate"));
const BecomeListerRealtor = lazy(() => import("@/pages/lister/BecomeListerRealtor"));
const ListerDashboard = lazy(() => import("@/pages/lister/ListerDashboard"));
const OwnerListingReview = lazy(() => import("@/pages/lister/OwnerListingReview"));
const ListingCreate = lazy(() => import("@/pages/lister/ListingCreate"));
const ListingEdit = lazy(() => import("@/pages/lister/ListingEdit"));
const BrowseRenters = lazy(() => import("@/pages/lister/BrowseRenters"));
const Applicants = lazy(() => import("@/pages/lister/Applicants"));
const ScreeningHub = lazy(() => import("@/pages/lister/ScreeningHub"));
const ScreeningDetail = lazy(() => import("@/pages/lister/ScreeningDetail"));
const BoostListing = lazy(() => import("@/pages/lister/BoostListing"));

// Lease room
const LeaseRoom = lazy(() => import("@/pages/lease/LeaseRoom"));

// Admin pages
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminListings = lazy(() => import("@/pages/admin/AdminListings"));
const AdminPayments = lazy(() => import("@/pages/admin/AdminPayments"));
const AdminScreenings = lazy(() => import("@/pages/admin/AdminScreenings"));
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
const AdminAnalytics = lazy(() => import("@/pages/admin/AdminAnalytics"));
const AdminModeration = lazy(() => import("@/pages/admin/AdminModeration"));
const AdminRealtors = lazy(() => import("@/pages/admin/AdminRealtors"));
const AdminPhotoRequests = lazy(() => import("@/pages/admin/AdminPhotoRequests"));

// Legal pages
const Terms = lazy(() => import("@/pages/legal/Terms"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));

// The ONLY place NavBottom is rendered. It lives at the router root — outside
// every page and every framer-motion wrapper — because a CSS transform on any
// ancestor re-anchors position:fixed to that ancestor instead of the viewport
// (this is exactly what broke the bar on real iOS). Route rules live here;
// the <md viewport rule lives inside NavBottom itself (md:hidden).
const NAV_BOTTOM_ROUTES = [
  /^\/$/,
  /^\/swipe$/,
  /^\/map$/,
  /^\/matches$/,
  /^\/messages$/,
  /^\/saved$/,
  /^\/likes$/,
  /^\/liked-you$/,
  /^\/hosts(\/|$)/,
  /^\/profile(\/|$)/,
  /^\/listing\//,
  /^\/lister(\/|$)/,
  /^\/pricing$/,
  /^\/how-it-works$/,
];

function NavBottomGate() {
  const { pathname } = useLocation();
  const show = NAV_BOTTOM_ROUTES.some((r) => r.test(pathname));
  return show ? <NavBottom /> : null;
}

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="h-10 w-10 animate-spin rounded-full border-[3px] border-brand-line border-t-brand-primary" />
    </div>
  );
}

// linx.swiprent.com is the admin host. On that hostname the app mounts the
// admin console at the ROOT ("/") and sends every non-admin path to the admin
// login/root instead of the consumer marketing UI. This is a UX convenience,
// NOT a security boundary: RequireAuth role="admin" is still what gates access,
// so an unauthed visitor to the admin host lands on the login page. isAdminHost
// lives in @/lib/utils so AdminLayout and the admin pages resolve the same
// mount base and build matching nav links.

// Shared admin child routes (rendered inside AdminLayout's <Outlet/>) so the
// same pages serve /admin on the main site and / on the admin host without
// duplication. Returns an array of <Route> elements.
const adminChildRoutes = [
  <Route key="index" index element={<AdminDashboard />} />,
  <Route key="users" path="users" element={<AdminUsers />} />,
  <Route key="photo-requests" path="photo-requests" element={<AdminPhotoRequests />} />,
  <Route key="realtors" path="realtors" element={<AdminRealtors />} />,
  <Route key="listings" path="listings" element={<AdminListings />} />,
  <Route key="moderation" path="moderation" element={<AdminModeration />} />,
  <Route key="payments" path="payments" element={<AdminPayments />} />,
  <Route key="screenings" path="screenings" element={<AdminScreenings />} />,
  <Route key="reports" path="reports" element={<AdminReports />} />,
  <Route key="analytics" path="analytics" element={<AdminAnalytics />} />,
];

function AdminHostApp() {
  return (
    <Routes>
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/" element={<RequireAuth role="admin"><AdminLayout /></RequireAuth>}>
        {adminChildRoutes}
      </Route>
      {/* Any other path on the admin host redirects to the admin root. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const adminHost = isAdminHost();
  return (
    <AuthProvider>
      <IntegrationsProvider>
      <LocationProvider>
        <FiltersProvider>
        <SavesProvider>
        <NotificationsProvider>
        <BrowserRouter>
          <ScrollToTop />
          <RouteSeo />
          <NativePushBridge />
          <Suspense fallback={<PageLoader />}>
            {adminHost ? (
              <AdminHostApp />
            ) : (
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/auth/login" element={<LoginPage />} />
              <Route path="/auth/signup" element={<SignupPage />} />
              <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/swipe" element={<RequireAuth><SwipePage /></RequireAuth>} />
              <Route path="/map" element={<MapPage />} />
              <Route path="/listing/:id" element={<ListingDetailPage />} />
              <Route path="/matches" element={<RequireAuth><MatchesPage /></RequireAuth>} />
              <Route path="/messages" element={<RequireAuth><MessagesPage /></RequireAuth>} />
              <Route path="/saved" element={<RequireAuth><SavedPage /></RequireAuth>} />
              <Route path="/likes" element={<RequireAuth><LikesPage /></RequireAuth>} />
              <Route path="/liked-you" element={<RequireAuth><LikedYouPage /></RequireAuth>} />
              <Route path="/hosts" element={<HostsPage />} />
              <Route path="/hosts/:id" element={<ListerPublicPage />} />
              <Route path="/invite/realtor/:token" element={<RealtorInvitePage />} />
              <Route path="/chat/:matchId" element={<RequireAuth><ChatPage /></RequireAuth>} />
              <Route path="/calendar" element={<RequireAuth><CalendarPage /></RequireAuth>} />
              <Route path="/profile" element={<RequireAuth><ProfileRenterPage /></RequireAuth>} />
              <Route path="/profile/lister" element={<RequireAuth><ProfileListerPage /></RequireAuth>} />
              <Route path="/checkout/:product" element={<RequireAuth><CheckoutPage /></RequireAuth>} />
              {/* Stripe redirect targets (success_url / cancel_url). These
                  MUST exist: paying users land here from Stripe. */}
              <Route path="/payments/success" element={<RequireAuth><PaymentResultPage variant="success" /></RequireAuth>} />
              <Route path="/payments/cancelled" element={<RequireAuth><PaymentResultPage variant="cancelled" /></RequireAuth>} />

              <Route path="/become-a-lister" element={<RequireAuth><BecomeLister /></RequireAuth>} />
              <Route path="/become-a-lister/private" element={<RequireAuth><BecomeListerPrivate /></RequireAuth>} />
              <Route path="/become-a-lister/realtor" element={<RequireAuth><BecomeListerRealtor /></RequireAuth>} />
              <Route path="/lister" element={<RequireLister><ListerDashboard /></RequireLister>} />
              <Route path="/lister/owned/:listingId" element={<RequireAuth><OwnerListingReview /></RequireAuth>} />
              <Route path="/lister/listings/new" element={<RequireLister><ListingCreate /></RequireLister>} />
              <Route path="/lister/new" element={<RequireLister><ListingCreate /></RequireLister>} />
              <Route path="/lister/listings/:id/edit" element={<RequireLister><ListingEdit /></RequireLister>} />
              <Route path="/lister/edit/:id" element={<RequireLister><ListingEdit /></RequireLister>} />
              <Route path="/lister/browse" element={<RequireLister><BrowseRenters /></RequireLister>} />
              <Route path="/lister/applicants" element={<RequireLister><Applicants /></RequireLister>} />
              {/* Screening is Coming soon everywhere (dashboard card, top
                  menu): direct URLs land back on the dashboard until launch.
                  Restore ScreeningHub/ScreeningDetail here when it ships. */}
              <Route path="/lister/screening" element={<Navigate to="/lister" replace />} />
              <Route path="/lister/screening/:screeningId" element={<Navigate to="/lister" replace />} />
              <Route path="/lister/boost/:listingId" element={<RequireLister><BoostListing /></RequireLister>} />

              <Route path="/lease/:matchId" element={<RequireAuth><LeaseRoom /></RequireAuth>} />

              <Route path="/admin" element={<RequireAuth role="admin"><AdminLayout /></RequireAuth>}>
                {adminChildRoutes}
              </Route>

              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            )}
          </Suspense>
          <NavBottomGate />
          <MarketPicker />
          <BotWidget />
          <PromoPopup />
          <VerifyEmailGate />
          {/* Notifications pop in centered at the top as branded cards —
              never tucked into a corner. One at a time keeps it calm. */}
          <Toaster
            position="top-center"
            visibleToasts={1}
            gap={10}
            toastOptions={{
              duration: 3500,
              classNames: {
                toast:
                  "rounded-2xl border border-brand-line bg-white shadow-ambient px-5 py-4 w-full sm:w-[380px] items-center justify-center",
                title: "text-sm font-semibold text-brand-ink text-center w-full",
                description: "text-xs text-brand-secondary text-center w-full",
                icon: "text-brand-primary",
              },
            }}
          />
        </BrowserRouter>
        </NotificationsProvider>
        </SavesProvider>
        </FiltersProvider>
      </LocationProvider>
      </IntegrationsProvider>
    </AuthProvider>
  );
}

export default App;
